import { useAuth0 } from '@auth0/auth0-react'
import { Link, graphql, useStaticQuery } from 'gatsby'
import React, { useContext } from 'react'

import useGeolocation from '../hooks/useGeolocation'
import { LocaleContext, MeasurLocale, localeLanguage } from '../locale/i18n'
import {
  compliancePagePath,
  localePrefix,
  staticPagePaths,
  testimonialPagePath,
} from '../locale/pathing'
import { StaticPage } from '../logic/staticPage'

import { EmailLink } from './EmailLink'
import IconSocialFacebook from './icons/IconSocialFacebook'
import IconSocialInstagram from './icons/IconSocialInstagram'
import IconSocialLinkedIn from './icons/IconSocialLinkedIn'
import IconSocialTwitter from './icons/IconSocialTwitter'
import LuotettavaKumppani from './icons/LuotettavaKumppani'
import { LoginButton } from './LoginButton'
import MeasurAddress from './MeasurAddress'
import MeasurLogo from './MeasurLogo'
import './Footer.scss'
import PhoneNumber, { defaultPhoneNumbersByCountry } from './PhoneNumber'

const BusinessHours = () => {
  const location = useGeolocation()
  const { t } = useContext(LocaleContext)
  if (location === 'GB') {
    return <p>{t('footer.businessHours.gb')}</p>
  } else {
    return <p>{t('footer.businessHours.fi')}</p>
  }
}

export const PureFooter = () => {
  const { locale, t } = useContext(LocaleContext)

  const { testimonialPage, compliancePage } = useStaticQuery<{
    testimonialPage: {
      id: string
    } | null
    compliancePage: {
      id: string
    } | null
  }>(graphql`
    query CombinedQuery {
      testimonialPage: contentfulLandingPage(slug: { eq: "testimonials" }) {
        id
      }
      compliancePage: contentfulLandingPage(
        slug: { eq: "compliance-and-quality" }
      ) {
        id
      }
    }
  `)

  const { user } = useAuth0()
  const isLoggedIn = Boolean(user?.sub)

  const renderLinks = () => [
    (
      [
        StaticPage.INDEX,
        StaticPage.PRODUCTS,
        StaticPage.METHODS,
        StaticPage.SOLUTIONS,
        StaticPage.CONTACT,
        StaticPage.PARTNERS,
        StaticPage.BLOG,
        StaticPage.CAREERS,
        StaticPage.SHIPPING_INSTRUCTIONS,
        StaticPage.TERMS_SALES,
        StaticPage.PRIVACY,
      ] as const
    ).map(page => (
      <Link
        className="footer__navLink"
        key={page}
        to={staticPagePaths[page][locale]}
      >
        {t(`nav.${page}`)}
      </Link>
    )),
    // extra links for non static pages
    testimonialPage && (
      <Link
        className="footer__navLink"
        key="testimonials"
        to={`${localePrefix(locale)}/${testimonialPagePath[locale]}/`}
      >
        {t('nav.testimonials')}
      </Link>
    ),
    compliancePage && (
      <Link
        className="footer__navLink"
        key="compliance-and-quality"
        to={`${localePrefix(locale)}/${compliancePagePath[locale]}/`}
      >
        {t('nav.complianceAndQuality')}
      </Link>
    ),
  ]

  return (
    <footer className="footer">
      <div className="footer__gutter">
        <div className="footer__grid">
          <div className="footer__column">
            <h2 className="footer__title">{t('footer.headings.nav')}</h2>
            {renderLinks()}
          </div>
          <div className="footer__column">
            <h2 className="footer__title">{t('footer.headings.contact')}</h2>
            <EmailLink email="info@measurlabs.com" location="footer">
              info@measurlabs.com
            </EmailLink>
            <PhoneNumber
              phoneNumbersByCountry={{
                FI: defaultPhoneNumbersByCountry.FI,
                GB: defaultPhoneNumbersByCountry.GB,
              }}
            />
            <h2 className="footer__title">
              {t('footer.headings.businessHours')}
            </h2>
            <BusinessHours />
            <div className="footer__socialIcons">
              {(
                [
                  {
                    ariaLabel: 'Facebook',
                    icon: <IconSocialFacebook />,
                    uri: 'https://www.facebook.com/measurlabs',
                  },
                  {
                    ariaLabel: 'LinkedIn',
                    icon: <IconSocialLinkedIn />,
                    uri: 'https://www.linkedin.com/company/measurlabs/',
                  },
                  {
                    ariaLabel: 'X',
                    icon: <IconSocialTwitter />,
                    uri: 'https://twitter.com/measurlabs',
                  },
                  {
                    ariaLabel: 'Instagram',
                    icon: <IconSocialInstagram />,
                    uri: 'https://www.instagram.com/measurlabs/',
                  },
                ] as const
              ).map(({ icon, uri, ariaLabel }, i) => (
                <a
                  className="footer__socialIcon"
                  href={uri}
                  key={i}
                  onClick={e => {
                    e.currentTarget.blur()
                  }}
                  aria-label={t(`footer.socialLinkAriaLabel.${ariaLabel}`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {icon}
                </a>
              ))}
            </div>
            <div className="footer__badges">
              <Link
                to={`${localePrefix(locale)}/${compliancePagePath[locale]}/`}
              >
                <img
                  loading="lazy"
                  src="/bureau-veritas-certified.webp"
                  className="h-15"
                  alt="Bureau Veritas Certified"
                />
              </Link>

              <a
                href={`https://zeckit.com/selvitys/FI/2820461-1?lang=${encodeURIComponent(localeLanguage(locale))}`}
              >
                {locale === MeasurLocale.FINLAND_FINNISH ? (
                  <LuotettavaKumppani />
                ) : (
                  <img
                    loading="lazy"
                    src="/reliable-partner.webp"
                    className="h-13"
                    alt="Reliable partner"
                  />
                )}
              </a>
            </div>
          </div>
          <div className="footer__column">
            <h2 className="footer__title">{t('footer.headings.address')}</h2>
            <MeasurAddress mapsLink className="footer__address" />
            {!isLoggedIn && (
              <>
                <h2 className="footer__title">{t('footer.headings.portal')}</h2>
                <p className="footer__portalIntro">{t('footer.portalIntro')}</p>
                <LoginButton />
              </>
            )}
          </div>
        </div>
        <div className="footer__socialRow" />
        <div className="footer__bottom">
          <p>Copyright {new Date().getFullYear()} Measur Oy (FI28204611)</p>
          <MeasurLogo className="footer__logo" textColor="#fff" />
        </div>
      </div>
    </footer>
  )
}

export default PureFooter
